"use client";

import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { useCurrentHref } from "@/hooks/use-current-href";
import { isSafeInternalHref, withFromParam } from "@/lib/back-nav";
import { captureCurrentRouteScroll } from "@/lib/navigation-scroll";
import { resolveShortLinkPath } from "@/lib/short-links/resolve-client";

/**
 * `router.push` with the `BackAwareLink` contract, for navigations that are
 * not an anchor (a whole-card `onClick`, a toast action, a button).
 *
 * A bare push stamps no `?from=` and saves no scroll position, so the
 * destination's back arrow falls through to its hard-coded parent and the
 * reader lands at the top of a page they were never on.
 */
export function useBackAwarePush() {
  const router = useRouter();
  const currentHref = useCurrentHref();

  return useCallback(
    (href: string, options?: { scroll?: boolean }) => {
      // External or unsafe targets are not ours to stamp.
      if (!isSafeInternalHref(href)) {
        router.push(href, options);
        return;
      }
      captureCurrentRouteScroll();
      if (href.startsWith("/l/")) {
        void resolveShortLinkPath(href).then((resolved) => {
          if (resolved)
            router.push(withFromParam(resolved, currentHref), options);
          else window.location.assign(withFromParam(href, currentHref));
        });
        return;
      }
      router.push(withFromParam(href, currentHref), options);
    },
    [currentHref, router],
  );
}
